// src/pages/ChatRoomUser.js
import React, { useEffect, useState } from 'react'
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  writeBatch,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage' 
import { db } from '../firebaseConfig'
import { ArrowLeft, Check, CheckCheck, Image as ImageIcon } from 'lucide-react'

const storage = getStorage()

export default function ChatRoomUser({ userName, chatId, onBack }) {
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)

  // Status open/closed (Tidak menggunakan session)
  const [sessionActive, setSessionActive] = useState(false) 
  
  const [isTyping, setIsTyping] = useState(false)
  const [typingTimeout, setTypingTimeout] = useState(null)

  // ======================================================
  // FUNGSI UPLOAD GAMBAR & KIRIM
  // ======================================================
  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!sessionActive) return alert('Chat sedang ditutup. Buka chat dulu.')
    if (!chatId) return

    try {
      setLoading(true)
      const storageRef = ref(storage, `chat_images/${chatId}/${Date.now()}_${file.name}`)
      const snapshot = await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)

      // Pesan User PASTI masuk langsung ke koleksi messages (Tanpa Sesi)
      const msgRef = collection(db, 'chats', chatId, 'messages')

      await addDoc(msgRef, {
        text: 'Mengirim gambar...', 
        imageUrl: downloadURL,      
        senderId: 'admin',
        senderName: 'Admin Zimo',
        isAdmin: true,
        type: 'image',              
        createdAt: serverTimestamp(),
        timestamp: serverTimestamp(),
      })

      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: '📷 [Gambar]',
        lastUpdated: serverTimestamp(),
        status: 'open' 
      })

    } catch (error) {
      console.error("Upload gagal:", error)
      alert("Gagal mengupload gambar.")
    } finally {
      setLoading(false)
    }
  }

  const renderMessageContent = (msg) => {
    if (msg.type === 'image' && msg.imageUrl) {
      return (
        <div className="mt-1">
          <img 
            src={msg.imageUrl} 
            alt="Sent attachment" 
            className="rounded-lg max-w-[200px] max-h-[200px] object-cover border border-gray-600 cursor-pointer"
            onClick={() => window.open(msg.imageUrl, '_blank')}
          />
        </div>
      )
    }

    const text = msg.text || ''
    const urlRegex = /(https?:\/\/[^\s]+)/g
    
    if (text.match(urlRegex)) {
      const parts = text.split(urlRegex)
      return (
        <span>
          {parts.map((part, i) => 
            part.match(urlRegex) ? (
              <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline hover:text-blue-300 break-all">
                {part}
              </a>
            ) : part
          )}
        </span>
      )
    }

    return text
  }

  // ======================================================
  // 1️⃣ Load Chat Meta (Direct Mode)
  // ======================================================
  useEffect(() => {
    if (!chatId) return

    const chatRef = doc(db, 'chats', chatId)
    
    // Kita anggap admin hadir jika panel ini terbuka
    updateDoc(chatRef, { adminPresent: true }).catch(() => {})

    const unsubMeta = onSnapshot(chatRef, (snap2) => {
      if (!snap2.exists()) return
      const data = snap2.data()
      // Untuk user, kita baca field 'status'
      setSessionActive(data.status === 'open' || data.sessionActive === true)
      
      // Deteksi user mengetik (jika ada field userTyping)
      setIsTyping(data.userTyping === true) 
    })
    
    return () => { 
      unsubMeta && unsubMeta() 
      updateDoc(chatRef, { adminPresent: false, adminTyping: false }).catch(() => {})
    }
  }, [chatId])

  // ======================================================
  // 2️⃣ Load Messages Listener (Direct Mode)
  // ======================================================
  useEffect(() => {
    if (!chatId) {
      setLoading(false)
      return
    }

    setLoading(true)
    const msgRef = collection(db, 'chats', chatId, 'messages')
    
    // Pesan User biasanya menggunakan 'createdAt' sebagai timestamp utama
    const msgQuery = query(msgRef, orderBy('createdAt', 'asc'))

    const unsubMsg = onSnapshot(msgQuery, (snapshot) => {
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().createdAt || d.data().timestamp
      }))
      setMessages(data)
      setLoading(false)

      snapshot.docs.forEach((docSnap) => {
        if (docSnap.data().senderId !== 'admin' && docSnap.data().status !== 'read') {
          updateDoc(docSnap.ref, { status: 'read' }).catch(() => {})
        }
      })
    })

    return () => unsubMsg()
  }, [chatId])
  
  // ======================================================
  // 3️⃣ ADMIN KIRIM PESAN 
  // ======================================================
  const handleSend = async () => {
    if (!newMessage.trim() || !chatId) return

    const msgRef = collection(db, 'chats', chatId, 'messages')

    const payload = {
      text: newMessage.trim(),
      senderId: 'admin', 
      senderName: 'Admin Zimo',
      isAdmin: true, 
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp(),
      type: 'text'
    }

    try {
      await addDoc(msgRef, payload)
      await updateDoc(doc(db, 'chats', chatId), {
        lastMessage: newMessage.trim(),
        lastUpdated: serverTimestamp(),
        lastTimestamp: serverTimestamp(), 
        status: 'open' // Pastikan status tetap open
      })
      setNewMessage('')
    } catch (e) {
       console.error("Gagal mengirim pesan:", e)
    }
  }

  // ======================================================
  // HAPUS HISTORY (METODE BATCH - KHUSUS USER)
  // ======================================================
  const deleteHistory = async () => {
    if (!chatId) return
    
    if (!window.confirm("PERINGATAN: Apakah Anda yakin? Semua pesan akan dihapus PERMANEN dan tidak bisa dikembalikan.")) return

    try {
      setLoading(true)
      
      const messagesRef = collection(db, 'chats', chatId, 'messages')
      const snapshot = await getDocs(messagesRef)

      if (snapshot.empty) {
        setLoading(false)
        return alert("Tidak ada pesan untuk dihapus.")
      }

      const batch = writeBatch(db)
      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref)
      })

      const chatDocRef = doc(db, 'chats', chatId)
      batch.update(chatDocRef, {
        lastMessage: 'Riwayat chat telah dibersihkan admin.',
        lastUpdated: serverTimestamp(),
        unreadByAdmin: 0
      })

      await batch.commit()

      alert("SUKSES! Semua riwayat chat telah bersih.")
      setMessages([]) 

    } catch (e) {
      console.error("Gagal hapus:", e)
      alert("Gagal menghapus: " + e.message)
    } finally {
      setLoading(false)
    }
  }

  // ======================================================
  // 4️⃣ ADMIN BUKA CHAT (User)
  // ======================================================
  const openChatUser = async () => {
    if (!chatId) return

    await updateDoc(doc(db, 'chats', chatId), { 
      status: 'open',
      waiting: false, 
      sessionActive: true,
      openedByAdmin: true
    })
    
    await addDoc(collection(db, 'chats', chatId, 'messages'), {
      senderId: 'system',
      text: 'Admin telah bergabung dalam chat.',
      createdAt: serverTimestamp(),
      type: 'system',
      isAdmin: true
    })

    alert("Status chat diubah menjadi OPEN. Silakan chat.")
  }

  // ======================================================
  // 5️⃣ ADMIN TUTUP CHAT (User)
  // ======================================================
  const closeChatUser = async () => {
    if (!chatId) return

    await updateDoc(doc(db, 'chats', chatId), { 
      status: 'closed',
      sessionActive: false,
      openedByAdmin: false
    })
    
    await addDoc(collection(db, 'chats', chatId, 'messages'), {
      senderId: 'system',
      text: 'Sesi chat telah ditutup oleh admin.',
      createdAt: serverTimestamp(),
      type: 'system',
      isAdmin: true
    })

    alert("Chat DITUTUP (Closed). Koneksi user terputus.")
  }

  if (loading)
    return (
      <div className="p-6 text-gray-500 text-sm text-center">
        Memuat percakapan...
      </div>
    )

  return (
    <div className="flex flex-col h-[calc(100vh-220px)] bg-gray-950 border border-gray-800 rounded-xl">
      {/* HEADER */}
      <div className="flex items-center justify-between bg-gray-900 px-4 py-3 border-b border-gray-800 rounded-t-xl">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-800 text-gray-300 transition"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <div className="flex items-center gap-2">
              <p className="text-gray-200 font-semibold text-lg">{userName}</p>
            </div>
            
            {/* 🔥 STATUS SESI & TYPING */}
            <p className="text-[12px] text-gray-400 font-medium flex items-center gap-1 mt-0.5">
              {sessionActive ? (isTyping ? <span className="text-blue-400 italic">sedang mengetik...</span> : <span className="text-green-400">Chat Terbuka</span>) : 'Chat Ditutup'}
            </p>
          </div>
        </div>

        {/* TOMBOL OPEN / CLOSE / DELETE */}
        <div className="flex gap-2">
          <button
            onClick={openChatUser}
            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs"
          >
            OPEN
          </button>

          <button
            onClick={closeChatUser}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
          >
            CLOSE
          </button>
          
          <button
            onClick={deleteHistory}
            className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs border border-gray-600"
            title="Hapus Riwayat Chat"
          >
            DELETE HISTORY
          </button>
        </div>
      </div>

      {/* PESAN */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-gray-500 text-sm text-center mt-10">
            Belum ada pesan.
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${
                msg.senderId === 'admin' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`px-4 py-2 rounded-2xl max-w-[70%] text-[15px] leading-relaxed flex flex-col ${
                  msg.senderId === 'admin'
                    ? 'bg-gray-800 text-gray-200 rounded-br-none shadow-md'
                    : 'bg-gray-900 text-gray-200 rounded-bl-none border border-gray-800 shadow-md'
                }`}
              >
                {renderMessageContent(msg)}

                {/* --- TIMESTAMP & CENTANG STATUS --- */}
                <div className={`flex items-center gap-1 mt-1 text-[10px] ${msg.senderId === 'admin' ? 'justify-end text-gray-400' : 'justify-start text-gray-500'}`}>
                  <span>{msg.timestamp?.toDate ? msg.timestamp.toDate().toLocaleTimeString('id-ID', {hour: '2-digit', minute:'2-digit'}) : ''}</span>
                  {msg.senderId === 'admin' && (
                    <span>
                      {msg.status === 'read' ? <CheckCheck size={14} className="text-blue-400" /> 
                      : msg.status === 'delivered' ? <CheckCheck size={14} className="text-gray-400" />
                      : <Check size={14} className="text-gray-500" />}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* INPUT */}
      <div className="flex items-center gap-2 p-3 bg-gray-900 border-t border-gray-800 rounded-b-xl">
        
        <label className={`p-2 rounded-full cursor-pointer transition ${sessionActive ? 'hover:bg-gray-800 text-gray-400' : 'opacity-50 cursor-not-allowed'}`}>
          <input 
            type="file" 
            accept="image/*" 
            className="hidden" 
            onChange={handleImageUpload}
            disabled={!sessionActive}
          />
          <ImageIcon size={20} />
        </label>

        <input
          value={newMessage}
          onChange={(e) => {
            setNewMessage(e.target.value)
            // Trigger Admin Sedang Mengetik
            if (chatId) {
              updateDoc(doc(db, 'chats', chatId), { adminTyping: true }).catch(() => {})
              if (typingTimeout) clearTimeout(typingTimeout)
              setTypingTimeout(setTimeout(() => updateDoc(doc(db, 'chats', chatId), { adminTyping: false }), 2000))
            }
          }}
          type="text"
          placeholder="Tulis pesan atau tempel Link..."
          onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-600 outline-none focus:ring-1 focus:ring-gray-700"
          disabled={!sessionActive}
        />
        <button
          onClick={handleSend}
          className="bg-gray-800 hover:bg-gray-700 text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition"
          disabled={!sessionActive}
        >
          Kirim
        </button>
      </div>
    </div>
  )
}